import React, { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../../context/AuthContext";
import {
  useDevOverride,
  type CurrentPeriod,
} from "../../context/DevOverrideContext";
import {
  getScheduleDisplayName,
  ScheduleConfigurationError,
  type ABDay,
} from "../../utils/scheduleUtils";
import {
  subscribeToScheduleVariants,
  subscribeToPeriodUpdates,
  hasEnoughTimeForPass as getRealTimeHasEnoughTime,
  isPassCreationAllowed as getRealTimeIsPassAllowed,
  timeToMinutes,
} from "../../utils/realTimeScheduleUtils";
import {
  showSuccess,
  showError,
  showWarning,
  showInfo,
} from "../../utils/notifications";
import {
  getContextualError,
  logError,
  PASS_ALREADY_UPDATED_MESSAGE,
} from "../../utils/errorHandling";
import {
  PASS_STATUS,
  listDestinations,
  createPass as createPassApi,
  cancelPass as cancelPassApi,
} from "../../api/studentPassesApi";
import { ApiError } from "../../api/http";
import { useStudentPassEvents } from "../../hooks/useStudentPassEvents";
import { usePassPolicy } from "../../hooks/usePassPolicy";
import MobileBlock from "../MobileBlock";
import ActivePassTimer from "./ActivePassTimer";
import PositionChangeNotification, {
  usePositionNotifications,
} from "./PositionChangeNotification";
import ScheduleErrorScreen from "../common/ScheduleErrorScreen";
import LoadingScreen from "../common/LoadingScreen";
import ConnectionStatusBanner from "../common/ConnectionStatusBanner";
import type { components } from "../../api/types.gen";
import styles from "./StudentDashboard.module.css";

type DestinationResponse = components["schemas"]["DestinationResponse"];

// The flattened period shape consumed by the pass-gating helpers
// (isPassCreationAllowed / hasEnoughTimeForPass), produced by
// toPassGatingPeriod below.
type PassGatingPeriod = {
  period: string | null;
  abDay: ABDay | string | null;
  minutes: number;
  timeRemaining: number;
};

// Human-readable labels for PassPolicy's PolicyInterval enum (DAY/WEEK/MONTH).
const POLICY_INTERVAL_LABELS = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month",
};

// Converts GET /schedule/today's `currentPeriod` (a PeriodWindowResponse -
// id/name/startTime/endTime/order/windowStart/windowEnd, "HH:MM" strings)
// into the { period, abDay, minutes, timeRemaining } shape
// isPassCreationAllowed/hasEnoughTimeForPass (realTimeScheduleUtils.js)
// expect. `abDay` is passed through separately (see abDayRef below) since
// GET /schedule/today's response doesn't carry it.
//
// NOTE: `abDay` above always resolves to null today. It comes from
// DevOverrideContext's getABDay(), which now reads realTimeScheduleUtils.js's
// REST-polled period cache (organizationData/{orgId}/systemSettings/
// currentPeriod's Firestore equivalent is gone) -- but GET /schedule/today
// itself has no abDay field, so the cache entry it populates always carries
// abDay: null. Threading abDay through here is forward-compatible plumbing
// for whenever the backend adds one; isPassCreationAllowed itself doesn't
// consume abDay (its blackout windows are hardcoded by period name only).
export function toPassGatingPeriod(
  period: { name: string | null; startTime: string; endTime: string },
  abDay: ABDay | string | null,
): PassGatingPeriod {
  const startTotalMinutes = timeToMinutes(period.startTime);
  const endTotalMinutes = timeToMinutes(period.endTime);

  // NOTE: `now` below is the browser's local wall clock, while
  // period.startTime/endTime are naive "HH:MM" strings resolved against
  // the school's configured timezone. GET /schedule/today never exposes
  // that timezone to the frontend - apps/schools-api/src/routes/
  // schedule.ts's resolveSchedule()/getCurrentTimeInTimezone() resolve
  // against school.timezone server-side but don't return it in the
  // response - so there's no way to convert `now` into the school's
  // timezone here. A device/school timezone mismatch will silently skew
  // timeRemaining/minutesIntoPeriod and the blackout-window math in
  // isPassCreationAllowed/hasEnoughTimeForPass that consume it. This is
  // a narrow, accepted edge case (school-issued devices are normally in
  // the school's timezone), documented rather than fixed here.
  const now = new Date();
  const nowTotalMinutes = now.getHours() * 60 + now.getMinutes();

  return {
    period: period.name,
    abDay,
    minutes: endTotalMinutes - startTotalMinutes,
    timeRemaining: Math.max(0, endTotalMinutes - nowTotalMinutes),
  };
}

// Backend PassStatus values are all-caps single words (PENDING, COMPLETED,
// ...) - render them in proper case instead of shouting them at the student.
function formatPassStatus(status: string): string {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

const StudentDashboard = (): React.ReactElement => {
  const { currentUser, logout } = useAuth();
  // schoolId is used as the org-identifier stand-in for the several utils
  // below (realTimeScheduleUtils, scheduleUtils, AnnouncementBanner,
  // ActivePassTimer). OrganizationContext itself still exists as a
  // deliberate compat shim (multi-org/Firestore orgs have no
  // hallpass-backend equivalent) - not an unmigrated TODO.
  const schoolId = currentUser?.schoolId;
  const {
    getCurrentPeriod,
    getScheduleType,
    getABDay,
    isAutoMode,
    isDeveloperMode,
    updateTrigger,
    scheduleVariantsMap,
  } = useDevOverride();

  const { passes, recentPasses, connectionStatus, lastEvent, refresh } =
    useStudentPassEvents();

  const [destinations, setDestinations] = useState<DestinationResponse[]>([]);
  const [destinationsError, setDestinationsError] = useState<string | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [destinationId, setDestinationId] = useState("");
  const [currentPeriod, setCurrentPeriod] = useState<CurrentPeriod | null>(
    null,
  );
  // Period used only to gate pass requests below (passCreationCheck /
  // hasEnoughTimeForPass) - fetched via REST, independently of
  // `currentPeriod` above (which drives the header's period/day/
  // schedule-type display via DevOverrideContext).
  const [passPeriod, setPassPeriod] = useState<PassGatingPeriod | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Schedule error state
  const [scheduleError, setScheduleError] = useState<Error | null>(null);

  // Initial loading state
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  const { notification, clearNotification, notifyDenied, notifyWaiting } =
    usePositionNotifications();

  // Subscribe to real-time schedule variant updates
  useEffect(() => {
    if (!schoolId) return;

    // Subscribe to schedule variants
    const unsubscribeSchedule = subscribeToScheduleVariants(
      schoolId,
      (variants) => {
        try {
          // Update current period with new schedule data
          const realTimePeriod = getCurrentPeriod();

          // Only show error if no schedule variants are configured at all
          if (Object.keys(variants).length === 0) {
            const configError = new ScheduleConfigurationError(
              "No schedule types configured - please contact your administrator",
            );
            setScheduleError(configError);
            setCurrentPeriod(null);
          } else {
            setCurrentPeriod(realTimePeriod);
            setScheduleError(null); // Clear any previous errors
          }
        } catch (error) {
          if (error instanceof ScheduleConfigurationError) {
            setScheduleError(error);
            setCurrentPeriod(null);
          } else {
            // Anything else was previously swallowed silently -- log it the
            // same way other failures in this file do so it isn't lost.
            logError(error, "scheduleVariantsSubscription");
          }
        }
      },
    );

    // Cleanup on unmount or school change
    return () => {
      unsubscribeSchedule();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId]); // Only depend on schoolId, not updateTrigger

  // Subscribe to the period currently in session, used only to gate pass
  // requests below (passCreationCheck / hasEnoughTimeForPass). Shares its
  // poll with the schedule-variants subscription above (both read GET
  // /schedule/today via realTimeScheduleUtils.js's orgPollers), so this
  // doesn't add a second request for the same org. GET /schedule/today uses
  // the same resolver POST /api/passes does, so `allowed` here stays in
  // sync with what the backend will actually accept.
  useEffect(() => {
    if (!schoolId) return;

    const unsubscribePeriod = subscribeToPeriodUpdates(
      schoolId,
      (periodData, error) => {
        if (error) {
          logError(error, "fetchCurrentSchedule");
          // Keep the last successfully-fetched passPeriod on transient
          // failure (matches useStudentPassEvents.js's history-fetch idiom)
          // rather than clearing it and spuriously blocking pass creation
          // for up to 30s.
          return;
        }

        setPassPeriod(
          periodData
            ? toPassGatingPeriod(
                {
                  name: periodData.period,
                  startTime: periodData.periodInfo.start,
                  endTime: periodData.periodInfo.end,
                },
                abDayRef.current,
              )
            : null,
        );
      },
    );

    return () => {
      unsubscribePeriod();
    };
  }, [schoolId]);

  // Update current period when DevOverrideContext's backend data changes
  useEffect(() => {
    const backendPeriod = getCurrentPeriod();
    setCurrentPeriod(backendPeriod);

    // Clear any errors when we get valid backend data
    if (backendPeriod) {
      setScheduleError(null);
    }
  }, [updateTrigger, getCurrentPeriod]); // Update when backend data changes

  // See the production-limitation note on toPassGatingPeriod above (~line
  // 45): getABDay() always returns null today, so abDayRef.current does
  // too. Kept as harmless forward-compatible plumbing.
  //
  // Mirrors getABDay()'s current value into a ref, kept fresh via
  // `updateTrigger` (DevOverrideContext's existing signal for "backend
  // data changed") rather than via getABDay's own identity. getABDay is a
  // useCallback whose identity changes on every backendPeriodState/
  // systemSettings change (and on every mock re-render in tests) - listing
  // it as a dependency of the 30s poll effect above would re-run that
  // effect and refetch on every such change. Reading this ref from the
  // poll's fetch closure instead avoids that entirely; refs are
  // intentionally excluded from React's dependency rules, so the poll
  // effect's own deps stay untouched.
  const abDayRef = useRef<ABDay | string | null>(null);
  useEffect(() => {
    abDayRef.current = getABDay();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateTrigger]);

  // Set initial loading to false once we have basic data
  useEffect(() => {
    if (currentUser && schoolId) {
      // Give a brief moment for UI to initialize
      const timer = setTimeout(() => {
        setIsInitialLoading(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [currentUser, schoolId]);

  // Fetch destinations once on mount. Unlike the old Firestore onSnapshot
  // listener, this is a one-time fetch: a destination added/renamed/removed
  // by an admin mid-session won't show up here until the student reloads or
  // taps "Retry" below. Accepted tradeoff for the REST migration.
  //
  // Guards against a stale response clobbering `destinations` for the wrong
  // schoolId: if schoolId changes while a fetch is in flight, the effect
  // below flips `cancelled` on cleanup so an earlier request that resolves
  // after a later one is dropped instead of overwriting state. Mirrors the
  // cancelled-flag pattern in useAdminSetupChecklist.js's fetchCounts.
  const fetchDestinations = useCallback(
    (isCancelled: () => boolean = () => false) => {
      if (!schoolId) return;
      if (!isCancelled()) setDestinationsError(null);
      listDestinations(schoolId)
        .then(({ data }) => {
          if (!isCancelled()) {
            setDestinations(
              [...data].sort((a, b) => a.name.localeCompare(b.name)),
            );
          }
        })
        .catch((error) => {
          logError(error, "fetchDestinations");
          if (!isCancelled()) {
            setDestinationsError(getContextualError(error, "pass-request"));
          }
        });
    },
    [schoolId],
  );

  useEffect(() => {
    let cancelled = false;
    fetchDestinations(() => cancelled);
    return () => {
      cancelled = true;
    };
  }, [fetchDestinations]);

  // Fetch the school's pass policy once on mount for the informational line
  // below the destination picker. There is no student-facing pre-flight
  // "can I request a pass" endpoint anymore — 422s from POST /passes are
  // the actual source of truth for whether a request will succeed.
  const { policy, error: policyError } = usePassPolicy(schoolId);

  useEffect(() => {
    if (policyError) {
      logError(policyError, "fetchSchoolPolicy");
    }
  }, [policyError]);

  // Notify the student when their pass is denied or put into WAITING
  // (destination full - the student is let in automatically once a slot
  // frees up). No toast on approval - there is no "approved" confirm-to-leave
  // stage anymore, the backend transitions straight to ACTIVE, and
  // ActivePassTimer renders below as soon as `passes` reflects that; that
  // transition is visible enough on its own. Depends only on lastEvent, not
  // notifyDenied/notifyWaiting - those functions' identity changes every
  // render (usePositionNotifications doesn't memoize them), which would
  // otherwise refire this on every unrelated render.
  useEffect(() => {
    if (lastEvent?.type === "pass:denied") {
      notifyDenied(lastEvent.pass?.denierNote);
    } else if (lastEvent?.type === "pass:waiting") {
      notifyWaiting(lastEvent.pass?.destinationName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastEvent]);

  const activePass = passes.find((p) => p.status === PASS_STATUS.ACTIVE);
  const waitingPass = passes.find((p) => p.status === PASS_STATUS.WAITING);
  const pendingPass = passes.find((p) => p.status === PASS_STATUS.PENDING);

  const policyInfoLines: string[] = [];
  if (policy?.maxActivePasses != null) {
    policyInfoLines.push(
      `Up to ${policy.maxActivePasses} active pass${policy.maxActivePasses === 1 ? "" : "es"} at a time schoolwide`,
    );
  }
  if (policy?.interval != null && policy?.maxPerInterval != null) {
    const intervalLabel =
      POLICY_INTERVAL_LABELS[policy.interval] || policy.interval.toLowerCase();
    policyInfoLines.push(
      `${policy.maxPerInterval} pass${policy.maxPerInterval === 1 ? "" : "es"} per ${intervalLabel}`,
    );
  }

  // Destinations no longer carry a countsTowardLimit flag (dropped - no
  // backend equivalent), so every destination is now subject to the same
  // period-timing restrictions below. This is a deliberate behavior change:
  // destinations that formerly bypassed timing limits no longer do.
  const passCreationCheck = getRealTimeIsPassAllowed(passPeriod, true);

  const submitPass = async () => {
    if (!destinationId) {
      showError("Please select a destination.");
      return;
    }

    // Prevent double submission by checking loading state immediately
    if (loading) {
      return;
    }

    if (!passCreationCheck.allowed) {
      showWarning(passCreationCheck.reason);
      return;
    }

    if (!getRealTimeHasEnoughTime(passPeriod, 15)) {
      showWarning(
        "Not enough time remaining in current period to request a pass.",
      );
      return;
    }

    setLoading(true);
    try {
      await createPassApi({ destinationId: Number(destinationId) });

      setDestinationId("");
      setSubmitError(null);
      showSuccess("Pass request submitted!");
      // Refresh from REST rather than waiting on the socket event alone, so
      // the pending card renders immediately.
      await refresh();
    } catch (error) {
      if (error instanceof ApiError && error.status === 422) {
        setSubmitError(error.message);
        showWarning(error.message);
      } else if (error instanceof ApiError && error.status === 409) {
        showInfo("You already have an active pass");
        await refresh();
      } else {
        logError(error, "submitPassRequest");
        showError(getContextualError(error, "pass-request"));
      }
    }
    setLoading(false);
  };

  const cancelPass = async (passId: number) => {
    if (!window.confirm("Cancel this pass? This action cannot be undone.")) {
      return;
    }

    try {
      await cancelPassApi(passId);
      showSuccess("Pass canceled successfully");
      await refresh();
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        await refresh();
        showWarning(PASS_ALREADY_UPDATED_MESSAGE);
      } else {
        logError(error, "cancelPass");
        showError(getContextualError(error, "pass-request"));
      }
    }
  };

  const handleDestinationChange = (
    e: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setDestinationId(e.target.value);
    setSubmitError(null);
  };

  // Check if user is on mobile device
  const isMobile =
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    );

  // Show initial loading screen
  if (isInitialLoading) {
    return <LoadingScreen message="Loading student dashboard..." />;
  }

  // Show schedule error screen if there's a configuration issue
  if (scheduleError) {
    return <ScheduleErrorScreen error={scheduleError} />;
  }

  // Show mobile block screen if on mobile
  if (isMobile) {
    return <MobileBlock />;
  }

  // Show timer screen if student has an active pass
  if (activePass) {
    return <ActivePassTimer pass={activePass} onReturned={refresh} />;
  }

  return (
    <div className={styles["dashboard"]}>
      <header>
        <div className="header-content">
          <div className="header-left">
            <h1>
              Student Dashboard
              {isDeveloperMode && (
                <span className={styles["dev-mode-badge"]}>🔧 DEV MODE</span>
              )}
            </h1>
            {currentUser?.school && (
              <div className="organization-info">
                <span className={styles["org-name"]}>
                  {currentUser.school.name}
                </span>
              </div>
            )}
          </div>
          <div className={styles["header-right"]}>
            <a
              href="https://docs.google.com/forms/d/e/1FAIpQLScdXhb7Qs0IOmSG3UoMLMqOP1CBpoSKFbdAe6iExkyrenIGqg/viewform?usp=header"
              target="_blank"
              rel="noopener noreferrer"
              className={styles["bug-report-link"]}
            >
              🐛 Report Bug
            </a>
            <button className={styles["logout-btn"]} onClick={logout}>
              Sign Out
            </button>
          </div>
        </div>
        {currentPeriod ? (
          <div className={styles["period-info"]}>
            <span className={styles["current-period"]}>
              {currentPeriod.displayName}
            </span>
            {/* GET /schedule/today carries no abDay, so this is null today;
                omit the "(_ Day)" suffix entirely rather than rendering a
                stray "( Day)". */}
            {currentPeriod.abDay && (
              <span className={styles["ab-day"]}>
                ({currentPeriod.abDay} Day)
              </span>
            )}
            <span className={styles["schedule-type"]}>
              •{" "}
              {scheduleVariantsMap[
                (currentPeriod.scheduleType || getScheduleType()) as string
              ] ||
                getScheduleDisplayName(
                  currentPeriod.scheduleType || getScheduleType(),
                )}
            </span>
            {isAutoMode && (
              <span className={styles["time-remaining"]}>
                • {currentPeriod.timeRemaining} min left
              </span>
            )}
          </div>
        ) : (
          <div className={styles["period-info"]}>
            <span className={styles["no-period"]}>No active period</span>
            {getABDay() && (
              <span className={styles["ab-day"]}>({getABDay()} Day)</span>
            )}
            {getScheduleType() && (
              <span className={styles["schedule-type"]}>
                •{" "}
                {scheduleVariantsMap[getScheduleType() as string] ||
                  getScheduleDisplayName(getScheduleType())}
              </span>
            )}
          </div>
        )}
      </header>

      <ConnectionStatusBanner connectionStatus={connectionStatus} />

      <main>
        {waitingPass && (
          <div className={`${styles["queued-pass"]} ${styles["waiting-pass"]}`}>
            <h2>⏳ Pass Approved - Waiting for Space</h2>
            <p>
              <strong>Destination:</strong> {waitingPass.destinationName}
              {waitingPass.note && ` — ${waitingPass.note}`}
            </p>
            {waitingPass.approverName && (
              <p>
                <strong>Approved by:</strong> {waitingPass.approverName}
              </p>
            )}

            <div className={styles["waiting-pass-actions"]}>
              <button
                onClick={() => cancelPass(waitingPass.id)}
                className={styles["cancel-pass-btn"]}
              >
                Cancel Pass
              </button>
            </div>

            {waitingPass.approvedAt && (
              <p>
                <small>
                  Approved:{" "}
                  {new Date(waitingPass.approvedAt).toLocaleTimeString()}
                </small>
              </p>
            )}
          </div>
        )}

        {pendingPass && (
          <div className={styles["pending-pass"]}>
            <h2>⏳ Pass Submitted</h2>
            <p>
              <strong>Destination:</strong> {pendingPass.destinationName}
              {pendingPass.note && ` — ${pendingPass.note}`}
            </p>
            <p>
              <strong>Status:</strong> Waiting for teacher approval
            </p>
            <p>
              <small>
                Requested:{" "}
                {new Date(pendingPass.requestedAt).toLocaleTimeString()}
              </small>
            </p>
            <button
              onClick={() => cancelPass(pendingPass.id)}
              className={styles["cancel-request-btn"]}
            >
              Cancel Request
            </button>
          </div>
        )}

        {!activePass && !pendingPass && !waitingPass && (
          <div className={styles["new-pass"]}>
            <h2>Request Hall Pass</h2>

            {!passCreationCheck.allowed ? (
              <div className={styles["temporal-restriction-status"]}>
                <div className={styles["restriction-notice"]}>
                  ⚠️ {passCreationCheck.reason}
                </div>
              </div>
            ) : (
              policyInfoLines.length > 0 && (
                <div className={styles["limits-info"]}>
                  {policyInfoLines.map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                </div>
              )
            )}

            <div className={styles["form-group"]}>
              <label htmlFor="destination">Destination:</label>
              <select
                id="destination"
                value={destinationId}
                onChange={handleDestinationChange}
                className={styles["destination-select"]}
              >
                <option value="">Select destination...</option>
                {destinations.map((dest) => (
                  <option key={dest.id} value={dest.id}>
                    {dest.name}
                  </option>
                ))}
              </select>
              {destinationsError && (
                <div className="destinations-error">
                  <small>🚫 {destinationsError}</small>{" "}
                  <button type="button" onClick={() => fetchDestinations()}>
                    Retry
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={submitPass}
              disabled={loading || !destinationId || !passCreationCheck.allowed}
              className={styles["submit-pass-btn"]}
              title={
                !passCreationCheck.allowed
                  ? passCreationCheck.reason
                  : loading
                    ? "Submitting..."
                    : ""
              }
            >
              {loading ? "Submitting..." : "Request Pass"}
            </button>

            {submitError && (
              <div className={styles["limits-restriction-notice"]}>
                <small>🚫 {submitError}</small>
              </div>
            )}
          </div>
        )}

        <div className={styles["pass-history"]}>
          <h3>Recent Passes</h3>
          {recentPasses.length === 0 ? (
            <p>No passes yet.</p>
          ) : (
            <ul>
              {recentPasses.slice(0, 5).map((pass) => (
                <li key={pass.id}>
                  <strong>{pass.destinationName}</strong> -{" "}
                  {formatPassStatus(pass.status)}
                  <br />
                  <small>{new Date(pass.requestedAt).toLocaleString()}</small>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      {/* Position Change Notification */}
      <PositionChangeNotification
        notification={notification}
        onClose={clearNotification}
      />
    </div>
  );
};

export default StudentDashboard;
